import React, { useState, useRef, useEffect } from 'react';
import { Ghost, RefreshCw, Undo2 } from 'lucide-react';
import { Analytics } from "@vercel/analytics/react";

const PumpkinCarvingApp = () => {
  const [isDrawing, setIsDrawing] = useState(false);
  const canvasRef = useRef(null);
  const contextRef = useRef(null);
  const imageRef = useRef(null);
  const [canvasStates, setCanvasStates] = useState([]);
  const [brushSize, setBrushSize] = useState(5);
  const [brushColor, setBrushColor] = useState('#FFA500');

  const brushSizes = [
    { name: 'S', value: 2 },
    { name: 'M', value: 5 },
    { name: 'L', value: 10 },
    { name: 'XL', value: 20 },
  ];

  const halloweenColors = [
    { name: 'Pumpkin Orange', value: '#FFA500' },
    { name: 'Ghost White', value: '#F8F8FF' },
    { name: 'Witch Green', value: '#1A4C1A' },
    { name: 'Vampire Red', value: '#C41E3A' },
    { name: 'Zombie Gray', value: '#808080' },
    { name: 'Candy Corn Yellow', value: '#FDFD96' },
  ];

  useEffect(() => {
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');
    canvas.width = Math.min(400, window.innerWidth - 40);
    canvas.height = canvas.width;
    context.lineCap = 'round';
    context.strokeStyle = brushColor;
    context.lineWidth = brushSize;
    contextRef.current = context;

    const img = new Image();
    img.crossOrigin = 'Anonymous';
    img.onload = () => {
      imageRef.current = img;
      context.drawImage(img, 0, 0, canvas.width, canvas.height);
      saveCanvasState();
    };
    img.src = 'https://i.ibb.co/BVP1PzW/pumpy.jpg';

    window.addEventListener('resize', resizeCanvas);

    return () => window.removeEventListener('resize', resizeCanvas);
  }, []);

  useEffect(() => {
    if (contextRef.current) {
      contextRef.current.strokeStyle = brushColor;
      contextRef.current.lineWidth = brushSize;
    }
  }, [brushColor, brushSize]);

  const saveCanvasState = () => {
    const canvas = canvasRef.current;
    setCanvasStates(prevStates => [...prevStates, canvas.toDataURL()]);
  };

  const resizeCanvas = () => {
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');
    const currentDrawing = context.getImageData(0, 0, canvas.width, canvas.height);
    canvas.width = Math.min(400, window.innerWidth - 40);
    canvas.height = canvas.width;
    context.putImageData(currentDrawing, 0, 0);
  };

  const startDrawing = ({ nativeEvent }) => {
    const { offsetX, offsetY } = nativeEvent;
    contextRef.current.beginPath();
    contextRef.current.moveTo(offsetX, offsetY);
    setIsDrawing(true);
  };

  const finishDrawing = () => {
    contextRef.current.closePath();
    setIsDrawing(false);
    saveCanvasState();
  };

  const draw = ({ nativeEvent }) => {
    if (!isDrawing) {
      return;
    }
    const { offsetX, offsetY } = nativeEvent;
    contextRef.current.lineTo(offsetX, offsetY);
    contextRef.current.stroke();
  };

  const handleRefresh = () => {
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');
    context.clearRect(0, 0, canvas.width, canvas.height);
    context.drawImage(imageRef.current, 0, 0, canvas.width, canvas.height);
    setCanvasStates([canvas.toDataURL()]);
  };

  const handleUndo = () => {
    if (canvasStates.length > 1) {
      const previousState = canvasStates[canvasStates.length - 2];
      const img = new Image();
      img.onload = () => {
        const canvas = canvasRef.current;
        const context = canvas.getContext('2d');
        context.clearRect(0, 0, canvas.width, canvas.height);
        context.drawImage(img, 0, 0);
      };
      img.src = previousState;
      setCanvasStates(prevStates => prevStates.slice(0, -1));
    }
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-orange-200">
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden z-0">
        {[...Array(20)].map((_, i) => (
          <Ghost 
            key={i}
            size={48} 
            className="text-white opacity-10 absolute animate-float"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDuration: `${5 + Math.random() * 10}s`,
              animationDelay: `${-Math.random() * 5}s`,
            }}
          />
        ))}
      </div>
      <div className="relative z-10 bg-orange-300 p-8 rounded-lg shadow-lg">
        <h1 className="text-4xl font-bold mb-4 text-orange-900">Pumpy Pumpkin Carver</h1>
        <canvas
          onMouseDown={startDrawing}
          onMouseUp={finishDrawing}
          onMouseMove={draw}
          ref={canvasRef}
          className="border-4 border-orange-400 rounded-lg shadow-md mb-4"
        />
        <p className="mb-4 text-orange-800 font-semibold">Click and drag to carve your pumpy pumpkin!</p>
        <div className="flex flex-col space-y-4">
          <div className="flex justify-center space-x-2">
            <p className="text-orange-800 font-semibold mr-2">Brush Size:</p>
            {brushSizes.map((size) => (
              <button
                key={size.value}
                onClick={() => setBrushSize(size.value)}
                className={`px-3 py-1 rounded ${
                  brushSize === size.value
                    ? 'bg-orange-500 text-white'
                    : 'bg-orange-200 text-orange-800 hover:bg-orange-400'
                }`}
              >
                {size.name}
              </button>
            ))}
          </div>
          <div className="flex justify-center items-center space-x-2">
            <p className="text-orange-800 font-semibold mr-2">Color:</p>
            {halloweenColors.map((color) => (
              <button
                key={color.value}
                onClick={() => setBrushColor(color.value)}
                className={`w-8 h-8 rounded-full border-2 ${
                  brushColor === color.value ? 'border-white' : 'border-transparent'
                }`}
                style={{ backgroundColor: color.value }}
                title={color.name}
              />
            ))}
          </div>
          <div className="flex justify-center space-x-4">
            <button
              onClick={handleUndo}
              className="bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded shadow-md transition duration-300 ease-in-out flex items-center"
              aria-label="Undo last action"
              disabled={canvasStates.length <= 1}
            >
              <Undo2 size={24} className="mr-2" /> Undo
            </button>
            <button
              onClick={handleRefresh}
              className="bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded shadow-md transition duration-300 ease-in-out flex items-center"
              aria-label="Refresh pumpkin"
            >
              <RefreshCw size={24} className="mr-2" /> Refresh
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <PumpkinCarvingApp />
      <Analytics />
    </div>
  );
}

export default App;